/**
 * @providesModule @api/Leagues
 */

//for pre-development TODO remove before prod or for real data
export const USE_MOCK = false;
export const MOCK_RESPONSE_DELAY = 500;
export const MOCK_FAIL_RESPONSE = false;
export const mockFailResponse = new Error('mock error for failing response');

// eslint-disable-next-line no-shadow
export enum LeagueType {
  Z = 'Z',
  CASH = 'CASH',
  BONUS_CASH = 'BONUS_CASH',
  LEVEL = 'LEVEL',
  TICKETZ = 'TICKETZ',
  CUSTOM = 'CUSTOM',
  MEDAL = 'MEDAL',
}

const MOCK_LEAGUE_AMOUNT_1 = 3000;
const MOCK_LEAGUE_AMOUNT_2 = 7000;
const MOCK_LEAGUE_AMOUNT_MULTIPLIER = 10;

export type League = {
  /**
   * The league instance id of a league template. Instance the player participates in.
   * @example 5678
   */
  league_instance_id: string;
  /**
   * The league id of the league template. Unique ID of the league that is different for every league season.
   * @example 1234
   */
  league_id: string;
  /**
   * The league set id of the league set the league is part of. The ID of the configuration describing the properties of the league set. This value stays the same during the lifetime of the league set.
   * @example 4242
   */
  league_set_configuration_id: string;
  /**
   * The ID of the league configuration that drive a specific league. Different leagues within a league sets will have different IDs, but these IDs stay the same over the seasons.
   * @example 9876
   */
  league_configuration_id: string;
  /**
   * The type of the league
   */
  type: LeagueType;
  /**
   * The duration of the league in seconds.
   * Used for fetching the concluded leagues before this duration.
   */
  duration?: number;
  /**
   * When the league end at
   * @type $date-time
   */
  ends_at: string;
  /**
   * How many games left to play to unlock the league
   */
  games_to_unlock: number;
  /**
   * How many games total to play to unlock the league
   * @example 5
   */
  total_games_to_unlock: number;
  /**
   * The label of the league (would be in the tabs)
   * @example Practice
   */
  label: string;
  /**
   * The title of the league (would be in the league info)
   * @example Practice League
   */
  title: string;
  /**
   * The description of the league (would be in the league info and FTUE)
   * @example Z Prizes for the top performing players!
   */
  description: string;
  /**
   * The league illustration image url
   * @type $uri
   * @example http://cdn.skillz.com/leagues/icons/some_image.png
   */
  icon_url: string; //($uri)
  /**
   * The medal used for the league
   * @type $uri
   * @example http://cdn.skillz.com/leagues/medals/md.png
   */
  medal: LeagueMedal;
  /**
   * The primary custom color of the league. Used for CTAs and leaderboard custom color
   * @example #4287f5
   * @nullable true
   */
  primary_color?: string | null;
  /**
   * The secondary custom color of the league. Used for League top tabs
   * @example #1243f5
   * @nullable true
   */
  secondary_color?: string | null;
  /**
   * The total value of all the prizes
   */
  total_prize_value: LeaguePrizeValue[];
  /**
   * an integer value from [1-100] configured on the league_id
   * level which tracks the progression of a user
   * across the various leagues_ids in a league_set
   */
  progression_priority: number;
  /**
   * If present, the league is a progressive league.
   * Progressive leagues are leagues that have a promotion/demotion system.
   */
  progressive_league?: ProgressiveLeague;
  /**
   * The list of prizes for the league
   */
  prizes: LeaguePrizeConfiguration[];
  /**
   * The array of carousel item on top of the leaderboard
   */
  carousel: LeagueCarouselItem[];
  /**
   * Additional field for the league.
   * Contains mainly cosmetic elements.
   * @nullable true
   */
  additional_fields?: {
    /** Theme mode of the color configuration. Should default to game theme mode on SDK side. */
    league_theme_mode?: 'light' | 'dark' | null;
  } | null;
  /**
   * The list of games that are contained within a single Global League Set.
   * Will include the current game_id as well. Only exists if the game_id is part of a Global League Set.
   */
  global_leaderboard?: GlobalLeaderboard[];
  /** An array of configured milestone sets for the league. Players will be segmented into a specific milestone set, but all sets are included in the data here for data mapping. */
  milestone_sets?: LeagueMilestoneSet[];
};

export type LeagueMilestoneSet = {
  /** The id of the milestone set. */
  milestone_set_id: string;
  /** The milestones that are part of the milestone set. Milestones are additional rewards that a player can earn by reaching a specific number of medals. */
  milestones: LeagueMilestone[];
};

export type LeagueMilestone = {
  /** The id of the milestone. */
  milestone_id: string;
  /** The prize configuration for the milestone. */
  prize_type: LeaguePrizeType;
  /** The amount of the prize. */
  prize_amount: number;
  /** The number of medals required to unlock the milestone. */
  medals_to_unlock: number;
};

/**
 * The prize configuration for a league.
 * Can be a unique prize or a range of prizes.
 * A unique prize is a prize that is awarded to a single place or Default prize.
 * A range of prizes is a prize that is awarded to a range of places,
 * this is only used for Progressive Leagues to support larger prize pools, usually around 10k+.
 */
export type LeaguePrizeConfiguration = LeaguePrizeDefault | LeaguePrizeUnique | LeaguePrizeRange;

export type LeaguePrizeDefault = {
  /**
   * The place the prize is for. Always -1 for default prize
   * This is considered the Default prize for all places not in the prizes array.
   * Meaning that all places taken up by users that are not in the prizes array will be awarded this prize.
   */
  place: -1;
  /**
   * The value configuration of the prize
   */
  value: LeaguePrizeValue;
};

export type LeaguePrizeUnique = {
  /**
   * The unique place the prize is for. Always sorted by place in ascending numerical order.
   */
  place: number;
  /**
   * The value configuration of the prize
   */
  value: LeaguePrizeValue;
};

export type LeaguePrizeRange = {
  /**
   * The start place of the prize range.
   * This is inclusive, meaning that the player with place >= place_start will receive the prize.
   */
  place_start: number;
  /**
   * The end place of the prize range
   * This is inclusive, meaning that the player with place <= place_end will receive the prize.
   */
  place_end: number;
  /**
   * The value configuration of the prize
   */
  value: LeaguePrizeValue;
};

export type LeagueMedal = {
  /**
   * The name of the medal
   * @example Pro medal
   */
  name: string;
  /**
   * The medal image used for the league
   * @type $uri
   * @example http://cdn.skillz.com/leagues/medals/md.png
   */
  url: string;
};

export enum LeaguePlayerPrizeStatus {
  ELIGIBLE = 'ELIGIBLE',
  CLAIMED = 'CLAIMED',
}

export type LeaguePlayer = {
  /**
   * The player user id
   * @example 1234
   */
  user_id: string;
  /**
   * The player's avatar or null if one is not specified for the user
   * @type $uri
   * @example https://cdn.qa.skillz.com/profile-pics/thumbnail/c8e7a21d-8c2c-4bbe-b17d-21
   * @nullable true
   */
  thumbnail_url?: string;
  /**
   * The player country flag
   * @type$uri
   * @example: https://cdn.qa.skillz.com/flags/US.png
   */
  flag_url: string;
  /**
   * The rank of the player. This can be zero on the currentPlayer object, but only if medals are also zero.
   * This case signifies that the currentPlayer has not been placed in a League yet, but will if they
   * earn medals.
   */
  place: number;
  /**
   * The display name of the player
   */
  name: string;
  /**
   * The number of medals within the league this user have
   * Can be null/undefined if the player didn't win any medal for this league
   * @nullable true
   */
  medals?: number;
  /**
   * The loyalty tier of this user. Used to set the avatar frame of the user.
   * Can be null/undefined.
   * @nullable true
   */
  tier_id?: number;
  /**
   * The status of a prize in the manual prize payout process.
   * @nullable true
   */
  prize_status?: LeaguePlayerPrizeStatus;
  /**
   * The breakdown of medals by game for the user. Only available if the
   * league the player is in is a part of Global Leagues.
   * @nullable true
   */
  medals_by_game?: MedalsByGame[];
};

export type MedalsByGame = {
  /**
   * The game id of the specific game within the global league
   */
  game_id: string;
  /**
   * The number of medals the player has earned in that game id
   */
  medals: number;
};

export type ProgressiveLeague = {
  /**
   * Number of medals that a participant should earn in order to get promoted to the next tier
   * Can be null when there is no instant promotion available in the league.
   */
  instant_promotion_medals: number | null;
  /**
   * What occurrence of the Progressive League this is. This number starts at 1 the first time a
   * Progressive League is created, and goes up by one each time a League finishes and a new one starts.
   */
  occurrence_number: number;
  /**
   * Minimum position/place on the Leaderboard required for demotion to the previous tier.
   * Any player at this position/place or below (meaning the player position/place is >= season_demotion_position)
   * will be demoted.
   * Can be null when the tier is the lowest tier in the set.
   */
  season_demotion_position: number | null;
  /**
   * Maximum position/place on the Leaderboard required for promotion to the next tier.
   * Any player at this position/place or above (meaning the player position/place is <= season_promotion_position)
   * will be promoted.
   * Can be null when the tier is the highest tier in the set.
   */
  season_promotion_position: number | null;
  /**
   * Represents the League tier within the set (0 represents the lowest tier;
     highest tier depends upon the number of tiers). This property is mandatory
     for every ProgressiveLeague object
    */
  tier: number;
  /**
   * The service fee configuration for a league, with an array of items each consisting of a max
   * prize and fee percentage. This percentage fee will be applied to a user's winnings upon the
   * completion of a match, if the total prize is less than or equal to the max_prize.
   * As of now, this can only apply to the highest tier of a Progressive League. For all other
   * leagues, this object will not exist.
   */
  service_fees?: ServiceFee[];
};

export type ServiceFee = {
  /**
   * The maximum prize pool for which the associated service fee is applicable.
   */
  max_prize: number;
  /**
   * fee_percentage is represented as percentage (1%, 10%, etc.)
   */
  fee_percentage: number;
};

// eslint-disable-next-line no-shadow
export enum LeaguePrizeType {
  VIRTUAL_CURRENCY = 'Z',
  CASH = 'CASH',
  BONUS_CASH = 'BONUS_CASH',
  TICKETZ = 'TICKETZ',
  // CUSTOM='CUSTOM'//physical price out of scope
}

export type LeaguePrizeValue = {
  /**
   * The type of prize
   */
  type: LeaguePrizeType;
  /**
   * The value amount of this prize
   */
  amount: number;
  /**
   * The currency of the prize (used in the case type is LeaguePrizeType.CASH or LeaguePrizeType.BONUS_CASH)
   * @type iso-4217
   * @example USD
   * @example INR
   */
  currency?: string;
};

export type LeaguePrizeClaimed = {
  /**
   * The user id of the player who claimed the prize
   */
  user_id: string;
  /**
   * The place of the player who claimed the prize
   */
  place?: number;
  /**
   * The prize claimed by the player
   */
  prize?: LeaguePrizeValue;
};

export type LeagueCarouselItem = {
  /**
   * Toggle to know if label tag is show on not
   */
  show_title: boolean;
  /**
   * The title under the label tag in the carousel item
   * @example Win this Porsche
   * @nullable true
   */
  subtitle?: string | null;
  /**
   * The description in the carousel item (under the title/subtitle)
   * @example 2021 911 Turbo
   */
  description: string;
  /**
   * The type of total prize to show in this carousel item. (to take from League.total_prize_value array)
   * If null, it indicates to SDK not to show a prize in the modal.
   * @nullable true
   * @deprecated use additional_fields.show_prize_types array instead
   */
  show_prize_type?:
    | LeaguePrizeType.VIRTUAL_CURRENCY
    | LeaguePrizeType.CASH
    | LeaguePrizeType.TICKETZ
    | null;
  /**
   * The image to show in the carousel item. If null, don't show any.
   * @type $uri
   * @example http://cdn.skillz.com/leagues/icons/some_image.png
   * @nullable true
   */
  icon_url?: string | null;
  /**
   * The background image of the carousel item
   * @example: http://cdn.skillz.com/leagues/backgrounds/bg.png
   * @deprecated made it optional during League 3.1
   */
  background_url?: string;
  /**
   * Additional fields for the carousel.
   * Currently contains the show_prize_types needed for the multi prize feature.
   * In the future, it could contain more information.
   * @nullable true
   */
  additional_fields?: {
    /** The array, in order, to show the multi prize. Replacing the use of the carousel root `show_prize_type`. */
    show_prize_types?:
      | (
          | LeaguePrizeType.VIRTUAL_CURRENCY
          | LeaguePrizeType.CASH
          | LeaguePrizeType.TICKETZ
          | LeaguePrizeType.BONUS_CASH
        )[]
      | null;
  } | null;
};

export type GlobalLeaderboard = {
  /**
   * The game id of the global game
   */
  game_id: string;
  /**
   * The game name of the global game
   */
  game_name: string;
  /**
   * The game icon url of the global game
   */
  game_icon_url: string;
};

//////////////////////////////////////////////////////////////////////////////
/******************************* MOCK ************************************* */

/** The mocked currency for League (change here to show one or the other) */
const mockCurrency = 'USD';

/**
 * Generate a mocked league for a given type
 * @param type The type of the league
 */
export const generateMockLeagueFromType = (
  type: LeagueType,
  override?: Partial<League>
): League => {
  switch (type) {
    case LeagueType.Z:
      return generateMockLeague({
        type,
        league_id: '456',
        label: 'Practice',
        title: 'Practice League',
        description: 'Compete against everyone! Finish in the top spots for BIG Z payouts',
        icon_url: 'https://imgur.com/CXaJeWS.png',
        total_prize_value: [
          generateMockLeaguePrize({
            type: LeaguePrizeType.VIRTUAL_CURRENCY,
            amount: MOCK_LEAGUE_AMOUNT_1,
          }),
        ],
        prizes: [
          {
            place: 1,
            value: generateMockLeaguePrize({amount: 1000, type: LeaguePrizeType.VIRTUAL_CURRENCY}),
          },
          {
            place: 2,
            value: generateMockLeaguePrize({amount: 500, type: LeaguePrizeType.VIRTUAL_CURRENCY}),
          },
          {
            place: 4,
            value: generateMockLeaguePrize({amount: 25, type: LeaguePrizeType.VIRTUAL_CURRENCY}),
          },
          {
            place: 8,
            value: generateMockLeaguePrize({amount: 10, type: LeaguePrizeType.VIRTUAL_CURRENCY}),
          },
        ],
        medal: {
          name: 'Practice medal',
          url: 'https://cdn.qa.skillz.com/portal-admin/silver-medal.png',
        },
        primary_color: '#7C278E',
        secondary_color: '#E27FF7',
        carousel: [
          {
            show_title: true,
            description: 'Z Prizes for the top performing players!',
            background_url: '',
            icon_url: 'https://imgur.com/CXaJeWS.png',
            additional_fields: {
              show_prize_types: [LeaguePrizeType.VIRTUAL_CURRENCY],
            },
          },
        ],
        ...override,
      });
    case LeagueType.CASH:
      return generateMockLeague({
        type,
        league_id: '123', //this one as default to open first
        label: 'Pro',
        title: 'Pro League',
        description: 'Compete against everyone! Finish in the top spots for BIG cash payouts',
        icon_url: 'https://imgur.com/8OfMu8A.png',
        total_prize_value: [
          generateMockLeaguePrize({type: LeaguePrizeType.CASH, amount: MOCK_LEAGUE_AMOUNT_2}),
        ],
        prizes: [
          {
            place: 1,
            value: generateMockLeaguePrize({amount: 2000, type: LeaguePrizeType.CASH}),
          },
          {
            place: 2,
            value: generateMockLeaguePrize({amount: 1000, type: LeaguePrizeType.CASH}),
          },
          {
            place: 3,
            value: generateMockLeaguePrize({amount: 500, type: LeaguePrizeType.CASH}),
          },
          {
            place: 4,
            value: generateMockLeaguePrize({amount: 25, type: LeaguePrizeType.CASH}),
          },
          {
            place: 10,
            value: generateMockLeaguePrize({amount: 10, type: LeaguePrizeType.CASH}),
          },
        ],
        medal: {
          name: 'Pro medal',
          url: 'https://cdn.skillz.com/portal-admin/gold-medal.png',
        },
        primary_color: '#295FA1',
        secondary_color: '#77ABEC',
        carousel: [
          {
            show_title: true,
            description: 'Cash Prizes for the top players!',
            background_url: '',
            icon_url: 'https://imgur.com/8OfMu8A.png',
            additional_fields: {
              show_prize_types: [LeaguePrizeType.CASH],
            },
          },
        ],
        ...override,
      });
    case LeagueType.BONUS_CASH:
      return {
        ...generateMockLeagueFromType(LeagueType.CASH, override), //same as cash
        type,
        league_id: '321', //need to be different than cash one
        //all prize bonus cash instead of cash
        total_prize_value: [
          generateMockLeaguePrize({type: LeaguePrizeType.BONUS_CASH, amount: 2000}),
        ],
        prizes: [
          {
            place: 1,
            value: generateMockLeaguePrize({amount: 1000, type: LeaguePrizeType.BONUS_CASH}),
          },
          {
            place: 2,
            value: generateMockLeaguePrize({amount: 500, type: LeaguePrizeType.BONUS_CASH}),
          },
          {
            place: 3,
            value: generateMockLeaguePrize({amount: 250, type: LeaguePrizeType.BONUS_CASH}),
          },
          {
            place: 4,
            value: generateMockLeaguePrize({amount: 10, type: LeaguePrizeType.BONUS_CASH}),
          },
          {
            place: 10,
            value: generateMockLeaguePrize({amount: 10, type: LeaguePrizeType.BONUS_CASH}),
          },
        ],
      };
    case LeagueType.TICKETZ: {
      return generateMockLeague({
        type,
        league_id: '789',
        label: 'Summer League',
        title: 'Summer event',
        description: 'Compete against everyone! Finish in the top spots for BIG cash payouts',
        icon_url: 'https://cdn.skillz.com/leagues/leaderboard/tier-06.svg',
        total_prize_value: [
          generateMockLeaguePrize({type: LeaguePrizeType.TICKETZ, amount: 100000}),
        ],
        prizes: [
          {
            place: 1,
            value: generateMockLeaguePrize({amount: 10000, type: LeaguePrizeType.TICKETZ}),
          },
          {
            place: 2,
            value: generateMockLeaguePrize({amount: 2000, type: LeaguePrizeType.TICKETZ}),
          },
          {
            place: 4,
            value: generateMockLeaguePrize({amount: 1000, type: LeaguePrizeType.TICKETZ}),
          },
          {
            place: 20,
            value: generateMockLeaguePrize({amount: 100, type: LeaguePrizeType.TICKETZ}),
          },
          {
            place: 30,
            value: generateMockLeaguePrize({amount: 100, type: LeaguePrizeType.TICKETZ}),
          },
        ],
        medal: {
          name: 'Custom medal',
          url: 'https://cdn-icons-png.flaticon.com/128/3176/3176367.png',
        },
        primary_color: '#bf0f85',
        secondary_color: '#bf0f85',
        carousel: [
          {
            show_title: true,
            description: 'Ticketz for the top performing players!',
            background_url: '',
            icon_url: 'https://cdn.pixabay.com/photo/2020/09/16/18/39/icon-5577198_960_720.png',
            additional_fields: {
              show_prize_types: [LeaguePrizeType.TICKETZ],
            },
          },
        ],
        ...override,
      });
    }
    case LeagueType.LEVEL: {
      const illustrationLevel =
        override?.league_id === 'L1'
          ? 'https://imgur.com/39DrEHo.png'
          : 'https://imgur.com/glK2JA4.png';
      const progression_priority =
        override?.league_id === 'L1'
          ? {progression_priority: 1}
          : override?.league_id === 'L2'
            ? {progression_priority: 2}
            : {};
      return generateMockLeague({
        type,
        league_id: 'L2',
        league_set_configuration_id: 'LEVEL',
        label: override?.league_id === 'L1' ? 'Lvl1' : 'Lvl2',
        title: 'Leveled League',
        description:
          'Compete against players in this League! Earn x medals to be promoted to next League for even better prizes',
        icon_url: illustrationLevel,
        total_prize_value: [
          generateMockLeaguePrize({
            type: LeaguePrizeType.BONUS_CASH,
            amount: override?.league_id === 'L1' ? MOCK_LEAGUE_AMOUNT_1 : MOCK_LEAGUE_AMOUNT_2,
          }),
          generateMockLeaguePrize({
            type: LeaguePrizeType.VIRTUAL_CURRENCY,
            amount:
              override?.league_id === 'L1'
                ? MOCK_LEAGUE_AMOUNT_1 * MOCK_LEAGUE_AMOUNT_MULTIPLIER
                : MOCK_LEAGUE_AMOUNT_2 * MOCK_LEAGUE_AMOUNT_MULTIPLIER,
          }),
        ],
        prizes: [
          {
            place: 1,
            value: generateMockLeaguePrize({amount: 1000, type: LeaguePrizeType.BONUS_CASH}),
          },
          {
            place: 2,
            value: generateMockLeaguePrize({amount: 500, type: LeaguePrizeType.BONUS_CASH}),
          },
          {
            place: 3,
            value: generateMockLeaguePrize({amount: 250, type: LeaguePrizeType.BONUS_CASH}),
          },
          {
            place: 4,
            value: generateMockLeaguePrize({amount: 10, type: LeaguePrizeType.BONUS_CASH}),
          },
          {
            place: 10,
            value: generateMockLeaguePrize({amount: 10, type: LeaguePrizeType.BONUS_CASH}),
          },
        ],
        medal: {
          name: 'Custom medal',
          url: 'https://cdn-icons-png.flaticon.com/128/3176/3176367.png',
        },
        primary_color: '#4B55AB',
        secondary_color: '#4B55BA',
        carousel: [
          {
            show_title: true,
            icon_url: illustrationLevel,
            background_url: '',
            description: 'Cash Prizes for the top players!',
            additional_fields: {
              show_prize_types: [LeaguePrizeType.CASH, LeaguePrizeType.VIRTUAL_CURRENCY],
            },
          },
        ],
        ...progression_priority,
        ...override,
      });
    }
    case LeagueType.CUSTOM:
      return generateMockLeague({
        type,
        league_id: '654',
        label: '100k Porsche',
        title: 'Porsche League',
        description:
          "Finish the Porsche League in the Top Spot and win a brand new Porsche! This is a limited time League, don't miss out!",
        icon_url: 'https://assets.stickpng.com/images/580b585b2edbce24c47b2caf.png',
        total_prize_value: [
          generateMockLeaguePrize({type: LeaguePrizeType.CASH, amount: 100000}),
          generateMockLeaguePrize({type: LeaguePrizeType.BONUS_CASH, amount: 1000}),
          generateMockLeaguePrize({type: LeaguePrizeType.TICKETZ, amount: 10000}),
          generateMockLeaguePrize({type: LeaguePrizeType.VIRTUAL_CURRENCY, amount: 100}),
        ],
        prizes: [
          {
            place: 1,
            value: generateMockLeaguePrize({amount: 100000, type: LeaguePrizeType.CASH}),
          },
          {
            place: 2,
            value: generateMockLeaguePrize({amount: 10000, type: LeaguePrizeType.BONUS_CASH}),
          },
          {
            place: 3,
            value: generateMockLeaguePrize({amount: 1000, type: LeaguePrizeType.TICKETZ}),
          },
          {
            place: 4,
            value: generateMockLeaguePrize({amount: 1000, type: LeaguePrizeType.VIRTUAL_CURRENCY}),
          },
        ],
        medal: {
          name: 'Custom medal',
          url: 'https://cdn-icons-png.flaticon.com/128/3176/3176367.png',
        },
        primary_color: '#c90e0e',
        secondary_color: '#c90e0e',
        carousel: [
          {
            subtitle: 'Win this Porsche',
            description: '2021 911 Turbo',
            background_url: 'https://robbreport.com/wp-content/uploads/2020/04/1-6.jpg?w=1000',
            show_title: true,
          },
          {
            show_title: false,
            subtitle: 'Win this Porsche',
            description: '2021 911 Turbo',
            background_url: 'https://robbreport.com/wp-content/uploads/2020/04/1-6.jpg?w=1000',
            icon_url:
              'https://i.pinimg.com/favicons/90d6e7c774e6201e6b47e6755c78a26b032309ed31e8c2e0a9da366f.png',
            additional_fields: {
              show_prize_types: [
                LeaguePrizeType.CASH,
                LeaguePrizeType.BONUS_CASH,
                LeaguePrizeType.TICKETZ,
                LeaguePrizeType.VIRTUAL_CURRENCY,
              ],
            },
          },
        ],
        ...override,
      });
    default:
      return generateMockLeague(override);
  }
};

export const getMockProgressiveLeagueLow = (): League => {
  const mock = {
    description: 'REMOVE',
    ends_at: '2023-08-11T23:15:00Z',
    games_to_unlock: 0,
    icon_url: 'https://cdn.skillz.com/leagues/leaderboard/tier-05.svg',
    label: 'Progressive League Test21B',
    league_configuration_id: '8eb381a3-665e-44ed-8a0c-28120e350d03',
    league_id: '8eb381a3-665e-44ed-8a0c-28120e350d03_21',
    league_instance_id: '8eb381a3-665e-44ed-8a0c-28120e350d03_21_0',
    league_set_configuration_id: '3b174166-e9e9-472c-95fd-b550a42d2bd7',
    medal: {
      name: 'Gold Medal',
      url: 'https://cdn.staging.skillz.com/portal-admin/gold-medal.png',
    },
    primary_color: '',
    prizes: [
      {
        place: 1,
        value: {
          amount: 35000,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 2,
        value: {
          amount: 30000,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 3,
        value: {
          amount: 25000,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 4,
        value: {
          amount: 24500,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 5,
        value: {
          amount: 24350,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 6,
        value: {
          amount: 24250,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 7,
        value: {
          amount: 24100,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 8,
        value: {
          amount: 24050,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 9,
        value: {
          amount: 24000,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 10,
        value: {
          amount: 23800,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 11,
        value: {
          amount: 23750,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 12,
        value: {
          amount: 23700,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 13,
        value: {
          amount: 23550,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 14,
        value: {
          amount: 23520,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 15,
        value: {
          amount: 23250,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 16,
        value: {
          amount: 23100,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 17,
        value: {
          amount: 23100,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 18,
        value: {
          amount: 23055,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 19,
        value: {
          amount: 23000,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 20,
        value: {
          amount: 22900,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 21,
        value: {
          amount: 22800,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 22,
        value: {
          amount: 22750,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 23,
        value: {
          amount: 22700,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 24,
        value: {
          amount: 22650,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 25,
        value: {
          amount: 22550,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 26,
        value: {
          amount: 22425,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 27,
        value: {
          amount: 22050,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 28,
        value: {
          amount: 22000,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 29,
        value: {
          amount: 22000,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 30,
        value: {
          amount: 21850,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 31,
        value: {
          amount: 21625,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 32,
        value: {
          amount: 21600,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 33,
        value: {
          amount: 21550,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 34,
        value: {
          amount: 21400,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 35,
        value: {
          amount: 21200,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 36,
        value: {
          amount: 21100,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 37,
        value: {
          amount: 21080,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 38,
        value: {
          amount: 20950,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 39,
        value: {
          amount: 20820,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 40,
        value: {
          amount: 20820,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 41,
        value: {
          amount: 20745,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 42,
        value: {
          amount: 20550,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 43,
        value: {
          amount: 20450,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 44,
        value: {
          amount: 20420,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 45,
        value: {
          amount: 20320,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 46,
        value: {
          amount: 20300,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 47,
        value: {
          amount: 20200,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 48,
        value: {
          amount: 20100,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 49,
        value: {
          amount: 20000,
          type: LeaguePrizeType.TICKETZ,
        },
      },
    ],
    progression_priority: 5,
    progressive_league: {
      instant_promotion_medals: 420,
      occurrence_number: 19,
      season_demotion_position: 31,
      season_promotion_position: 10,
      tier: 4,
    },
    secondary_color: '',
    carousel: [
      {
        subtitle: 'Win this Porsche',
        description: '2021 911 Turbo',
        background_url: 'https://robbreport.com/wp-content/uploads/2020/04/1-6.jpg?w=1000',
        show_title: true,
      },
      {
        show_title: false,
        subtitle: 'Win this Porsche',
        description: '2021 911 Turbo',
        background_url: 'https://robbreport.com/wp-content/uploads/2020/04/1-6.jpg?w=1000',
        icon_url:
          'https://i.pinimg.com/favicons/90d6e7c774e6201e6b47e6755c78a26b032309ed31e8c2e0a9da366f.png',
        additional_fields: {
          show_prize_types: [
            LeaguePrizeType.CASH,
            LeaguePrizeType.BONUS_CASH,
            LeaguePrizeType.TICKETZ,
            LeaguePrizeType.VIRTUAL_CURRENCY,
          ],
        },
      },
    ],
    title: 'Veteran',
    total_games_to_unlock: 5,
    total_prize_value: [
      {
        amount: 1880830,
        type: LeaguePrizeType.TICKETZ,
      },
    ],
    type: LeagueType.MEDAL,
  };

  return mock as League;
};

export const getMockProgressiveLeagueMedium = (): League => {
  const mock = {
    description: 'REMOVE',
    ends_at: '2023-08-11T23:15:00Z',
    games_to_unlock: 0,
    icon_url: 'https://cdn.skillz.com/leagues/leaderboard/tier-05.svg',
    label: 'Progressive League Test21B',
    league_configuration_id: '8eb381a3-665e-44ed-8a0c-28120e350d03',
    league_id: '8eb381a3-665e-44ed-8a0c-28120e350d03_21',
    league_instance_id: '8eb381a3-665e-44ed-8a0c-28120e350d03_21_0',
    league_set_configuration_id: '3b174166-e9e9-472c-95fd-b550a42d2bd7',
    medal: {
      name: 'Gold Medal',
      url: 'https://cdn.staging.skillz.com/portal-admin/gold-medal.png',
    },
    primary_color: '',
    prizes: [
      {
        place: 1,
        value: {
          amount: 35000,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 2,
        value: {
          amount: 30000,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 3,
        value: {
          amount: 25000,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 4,
        value: {
          amount: 24500,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 5,
        value: {
          amount: 24350,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 6,
        value: {
          amount: 24250,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 7,
        value: {
          amount: 24100,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 8,
        value: {
          amount: 24050,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 9,
        value: {
          amount: 24000,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 10,
        value: {
          amount: 23800,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 11,
        value: {
          amount: 23750,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 12,
        value: {
          amount: 23700,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 13,
        value: {
          amount: 23550,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 14,
        value: {
          amount: 23520,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 15,
        value: {
          amount: 23250,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 16,
        value: {
          amount: 23100,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 17,
        value: {
          amount: 23100,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 18,
        value: {
          amount: 23055,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 19,
        value: {
          amount: 23000,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 20,
        value: {
          amount: 22900,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 21,
        value: {
          amount: 22800,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 22,
        value: {
          amount: 22750,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 23,
        value: {
          amount: 22700,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 24,
        value: {
          amount: 22650,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 25,
        value: {
          amount: 22550,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 26,
        value: {
          amount: 22425,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 27,
        value: {
          amount: 22050,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 28,
        value: {
          amount: 22000,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 29,
        value: {
          amount: 22000,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 30,
        value: {
          amount: 21850,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 31,
        value: {
          amount: 21625,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 32,
        value: {
          amount: 21600,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 33,
        value: {
          amount: 21550,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 34,
        value: {
          amount: 21400,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 35,
        value: {
          amount: 21200,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 36,
        value: {
          amount: 21100,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 37,
        value: {
          amount: 21080,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 38,
        value: {
          amount: 20950,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 39,
        value: {
          amount: 20820,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 40,
        value: {
          amount: 20820,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 41,
        value: {
          amount: 20745,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 42,
        value: {
          amount: 20550,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 43,
        value: {
          amount: 20450,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 44,
        value: {
          amount: 20420,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 45,
        value: {
          amount: 20320,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 46,
        value: {
          amount: 20300,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 47,
        value: {
          amount: 20200,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 48,
        value: {
          amount: 20100,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 49,
        value: {
          amount: 20000,
          type: LeaguePrizeType.TICKETZ,
        },
      },
    ],
    progression_priority: 5,
    progressive_league: {
      instant_promotion_medals: 420,
      occurrence_number: 21,
      season_demotion_position: 31,
      season_promotion_position: 10,
      tier: 5,
    },
    secondary_color: '',
    carousel: [
      {
        subtitle: 'Win this Porsche',
        description: '2021 911 Turbo',
        background_url: 'https://robbreport.com/wp-content/uploads/2020/04/1-6.jpg?w=1000',
        show_title: true,
      },
      {
        show_title: false,
        subtitle: 'Win this Porsche',
        description: '2021 911 Turbo',
        background_url: 'https://robbreport.com/wp-content/uploads/2020/04/1-6.jpg?w=1000',
        icon_url:
          'https://i.pinimg.com/favicons/90d6e7c774e6201e6b47e6755c78a26b032309ed31e8c2e0a9da366f.png',
        additional_fields: {
          show_prize_types: [
            LeaguePrizeType.CASH,
            LeaguePrizeType.BONUS_CASH,
            LeaguePrizeType.TICKETZ,
            LeaguePrizeType.VIRTUAL_CURRENCY,
          ],
        },
      },
    ],
    title: 'Hero',
    total_games_to_unlock: 5,
    total_prize_value: [
      {
        amount: 1880830,
        type: LeaguePrizeType.TICKETZ,
      },
    ],
    type: LeagueType.MEDAL,
  };

  return mock as League;
};

export const getMockProgressiveLeagueHigh = (): League => {
  const mock = {
    description: 'REMOVE',
    ends_at: '2023-08-11T23:15:00Z',
    games_to_unlock: 0,
    icon_url: 'https://cdn.skillz.com/leagues/leaderboard/tier-06.svg',
    label: 'Progressive League Test21B',
    league_configuration_id: '8eb381a3-665e-44ed-8a0c-28120e350d03',
    league_id: '8eb381a3-665e-44ed-8a0c-28120e350d03_21',
    league_instance_id: '8eb381a3-665e-44ed-8a0c-28120e350d03_21_0',
    league_set_configuration_id: '3b174166-e9e9-472c-95fd-b550a42d2bd7',
    medal: {
      name: 'Gold Medal',
      url: 'https://cdn.staging.skillz.com/portal-admin/gold-medal.png',
    },
    primary_color: '',
    prizes: [
      {
        place: 1,
        value: {
          amount: 35000,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 2,
        value: {
          amount: 30000,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 3,
        value: {
          amount: 25000,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 4,
        value: {
          amount: 24500,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 5,
        value: {
          amount: 24350,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 6,
        value: {
          amount: 24250,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 7,
        value: {
          amount: 24100,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 8,
        value: {
          amount: 24050,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 9,
        value: {
          amount: 24000,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 10,
        value: {
          amount: 23800,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 11,
        value: {
          amount: 23750,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 12,
        value: {
          amount: 23700,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 13,
        value: {
          amount: 23550,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 14,
        value: {
          amount: 23520,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 15,
        value: {
          amount: 23250,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 16,
        value: {
          amount: 23100,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 17,
        value: {
          amount: 23100,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 18,
        value: {
          amount: 23055,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 19,
        value: {
          amount: 23000,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 20,
        value: {
          amount: 22900,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 21,
        value: {
          amount: 22800,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 22,
        value: {
          amount: 22750,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 23,
        value: {
          amount: 22700,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 24,
        value: {
          amount: 22650,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 25,
        value: {
          amount: 22550,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 26,
        value: {
          amount: 22425,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 27,
        value: {
          amount: 22050,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 28,
        value: {
          amount: 22000,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 29,
        value: {
          amount: 22000,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 30,
        value: {
          amount: 21850,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 31,
        value: {
          amount: 21625,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 32,
        value: {
          amount: 21600,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 33,
        value: {
          amount: 21550,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 34,
        value: {
          amount: 21400,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 35,
        value: {
          amount: 21200,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 36,
        value: {
          amount: 21100,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 37,
        value: {
          amount: 21080,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 38,
        value: {
          amount: 20950,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 39,
        value: {
          amount: 20820,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 40,
        value: {
          amount: 20820,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 41,
        value: {
          amount: 20745,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 42,
        value: {
          amount: 20550,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 43,
        value: {
          amount: 20450,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 44,
        value: {
          amount: 20420,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 45,
        value: {
          amount: 20320,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 46,
        value: {
          amount: 20300,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 47,
        value: {
          amount: 20200,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 48,
        value: {
          amount: 20100,
          type: LeaguePrizeType.TICKETZ,
        },
      },
      {
        place: 49,
        value: {
          amount: 20000,
          type: LeaguePrizeType.TICKETZ,
        },
      },
    ],
    progression_priority: 5,
    progressive_league: {
      instant_promotion_medals: 420,
      occurrence_number: 17,
      season_demotion_position: 31,
      season_promotion_position: 10,
      tier: 6,
    },
    secondary_color: '',
    carousel: [
      {
        subtitle: 'Win this Porsche',
        description: '2021 911 Turbo',
        background_url: 'https://robbreport.com/wp-content/uploads/2020/04/1-6.jpg?w=1000',
        show_title: true,
      },
      {
        show_title: false,
        subtitle: 'Win this Porsche',
        description: '2021 911 Turbo',
        background_url: 'https://robbreport.com/wp-content/uploads/2020/04/1-6.jpg?w=1000',
        icon_url:
          'https://i.pinimg.com/favicons/90d6e7c774e6201e6b47e6755c78a26b032309ed31e8c2e0a9da366f.png',
        additional_fields: {
          show_prize_types: [
            LeaguePrizeType.CASH,
            LeaguePrizeType.BONUS_CASH,
            LeaguePrizeType.TICKETZ,
            LeaguePrizeType.VIRTUAL_CURRENCY,
          ],
        },
      },
    ],
    title: 'Champion',
    total_games_to_unlock: 5,
    total_prize_value: [
      {
        amount: 1880830,
        type: LeaguePrizeType.TICKETZ,
      },
    ],
    type: LeagueType.MEDAL,
  };

  return mock as League;
};

export const generateMockLeague = (override?: Partial<League>): League => {
  // generate random number between 1-100
  // eslint-disable-next-line no-magic-numbers
  const randomInt = Math.floor(Math.random() * 100) + 1;
  const progression_priority = override?.type === LeagueType.LEVEL ? randomInt : 0;
  return {
    league_instance_id: '5678',
    league_id: '1234',
    league_set_configuration_id: '4242',
    league_configuration_id: '9876',
    type: LeagueType.Z,
    ends_at: '2023-07-22T22:19:40.807Z',
    games_to_unlock: 0,
    total_games_to_unlock: 5,
    label: 'Practice',
    title: 'Practice League',
    progression_priority,
    description: 'Z Prizes for the top performing players!',
    icon_url: 'https://imgur.com/CXaJeWS.png',
    medal: {
      name: 'Pro medal',
      url: 'https://cdn.skillz.com/portal-admin/gold-medal.png',
    },
    primary_color: '#4287f5',
    secondary_color: '#1243f5',
    total_prize_value: [
      generateMockLeaguePrize({
        type: LeaguePrizeType.CASH,
        amount: 0,
        currency: mockCurrency,
      }),
      generateMockLeaguePrize(),
    ],

    prizes: generateMockPrizesWithBigGap(),
    carousel: [
      generateMockLeagueCarouselItem({
        show_title: false,
        additional_fields: {
          show_prize_types: null,
        },
      }),
      generateMockLeagueCarouselItem(),
    ],
    global_leaderboard: [],
    ...override,
  } as League;
};

export const generateMockPrizeListWithThreeAndFiveMissing = (): LeaguePrizeConfiguration[] => {
  return [
    {
      place: 1,
      value: generateMockLeaguePrize({
        amount: 100,
      }),
    },
    {
      place: 2,
      value: generateMockLeaguePrize({
        amount: 50,
      }),
    },
    {
      place: 4,
      value: generateMockLeaguePrize({
        amount: 10,
      }),
    },
    {
      place: 6,
      value: generateMockLeaguePrize({
        amount: 10,
      }),
    },
  ];
};
export const generateMockPrizesWithBigGap = (): LeaguePrizeConfiguration[] => {
  return [
    {
      place: 1,
      value: generateMockLeaguePrize({
        amount: 100,
      }),
    },
    {
      place: 2,
      value: generateMockLeaguePrize({
        amount: 50,
      }),
    },
    {
      place: 4,
      value: generateMockLeaguePrize({
        amount: 25,
      }),
    },
    {
      place: 8,
      value: generateMockLeaguePrize({
        amount: 10,
      }),
    },
  ];
};
export const generateMockPrizesWithBigTwoThousand = (): LeaguePrizeConfiguration[] => {
  return [
    {
      place: 1,
      value: generateMockLeaguePrize({
        amount: 100,
      }),
    },
    {
      place: 2,
      value: generateMockLeaguePrize({
        amount: 50,
      }),
    },
    {
      place: 4,
      value: generateMockLeaguePrize({
        amount: 25,
      }),
    },
    {
      place: 2000,
      value: generateMockLeaguePrize({
        amount: 10,
      }),
    },
  ];
};
export const generateMockPrizeListWithNoneMissing = (): LeaguePrizeConfiguration[] => {
  return [
    {
      place: 1,

      value: generateMockLeaguePrize({
        amount: 15,
      }),
    },
    {
      place: 2,
      value: generateMockLeaguePrize({
        amount: 10,
      }),
    },
  ];
};

export const generateMockPrizeListWithDefaultPrize = (): LeaguePrizeConfiguration[] => {
  return [
    {
      place: -1, // default prize
      value: generateMockLeaguePrize({
        amount: 1,
      }),
    },
    {
      place: 1,
      value: generateMockLeaguePrize({
        amount: 15,
      }),
    },
    {
      place: 2,
      value: generateMockLeaguePrize({
        amount: 10,
      }),
    },
  ];
};

export const generateMockPrizeListWithRanges = (): LeaguePrizeConfiguration[] => {
  return [
    {
      place: 1, // first place gets top prize
      value: generateMockLeaguePrize({
        amount: 100,
      }),
    },
    {
      place_start: 2,
      place_end: 3,
      value: generateMockLeaguePrize({
        amount: 50,
      }),
    },
    {
      place_start: 4,
      place_end: 10,
      value: generateMockLeaguePrize({
        amount: 10,
      }),
    },
  ];
};

export const generateMockLeaguePlayer = (override?: Partial<LeaguePlayer>): LeaguePlayer => {
  return {
    user_id: '1234',
    thumbnail_url: 'https://cdn.qa.skillz.com/profile-pics/thumbnail/c8e7a21d-8c2c-4bbe-b17d-21',
    flag_url: 'https://cdn.qa.skillz.com/flags/US.png',
    place: 1,
    name: 'string',
    medals: 0,
    ...override,
  };
};
export const generateMockCustomLeaguePlayer = (
  id: string,
  place: number,
  override?: Partial<LeaguePlayer>
): LeaguePlayer => {
  return generateMockLeaguePlayer({
    user_id: id,
    place: place,
    ...override,
  });
};

export const generateMockLeaguePrize = (override?: Partial<LeaguePrizeValue>): LeaguePrizeValue => {
  return {
    type: LeaguePrizeType.VIRTUAL_CURRENCY,
    amount: 0,
    currency: mockCurrency,
    ...override,
  };
};

export const generateMockLeagueCarouselItem = (
  override?: Partial<LeagueCarouselItem>
): LeagueCarouselItem => {
  return {
    show_title: true,
    subtitle: 'Win this Porsche',
    description: '2021 911 Turbo',
    additional_fields: {
      show_prize_types: [LeaguePrizeType.CASH],
    },
    icon_url:
      'https://i.pinimg.com/favicons/90d6e7c774e6201e6b47e6755c78a26b032309ed31e8c2e0a9da366f.png',
    background_url: 'https://robbreport.com/wp-content/uploads/2020/04/1-6.jpg?w=1000',
    ...override,
  };
};

export const generateMockPrizes = (
  length: number,
  type?: LeaguePrizeType
): {place: number; value: LeaguePrizeValue}[] => {
  return Array.from({length}, (_, i) => ({
    place: i + 1,
    value: {
      amount: length - i,
      type: type ?? LeaguePrizeType.CASH,
      currency: !type || type === LeaguePrizeType.CASH ? mockCurrency : undefined,
    },
  }));
};

export const generateMockPrizesWithRanges = (
  length: number,
  type?: LeaguePrizeType,
  prizeRanges?: LeaguePrizeRange[]
): LeaguePrizeConfiguration[] => {
  let prizes = Array.from({length}, (_, i) => ({
    place: i + 1,
    value: {
      amount: length - i,
      type: type ?? LeaguePrizeType.CASH,
      currency: !type || type === LeaguePrizeType.CASH ? mockCurrency : undefined,
    },
  })) as LeaguePrizeConfiguration[];

  if (prizeRanges) {
    prizes = prizes.reduce((acc: LeaguePrizeConfiguration[], prize) => {
      const prizeRange = prizeRanges.find(
        (range) =>
          'place' in prize && range.place_start <= prize.place && range.place_end >= prize.place
      );

      if (prizeRange) {
        const prizeRangeAlreadyExists = acc.find(
          (range) =>
            'place_start' in range &&
            'place_end' in range &&
            range.place_start === prizeRange.place_start &&
            range.place_end === prizeRange.place_end
        );

        return prizeRangeAlreadyExists ? acc : [...acc, prizeRange];
      } else {
        return [...acc, prize];
      }
    }, []);
  }

  return prizes;
};
